import { Text, Box, Code, Image, Group, ActionIcon, Stack, Divider } from '@mantine/core';
import { IconVolume } from '@tabler/icons-react';
import type { FlashcardContent, ContentType, Flashcard } from '../types/flashcard';
import '../components/FlashcardViewer.css';

/**
 * Content Display Configuration
 * Each content type has its own display logic for different contexts
 */

// ==================== Preview Display (for tables/lists) ====================

interface ContentPreview {
  main: string;
  subtitle?: string;
}

/**
 * Get a short preview of content for display in tables/lists
 */
export function getContentPreview(content: FlashcardContent): ContentPreview {
  switch (content.content_type) {
    case 'text':
      return formatTextPreview(content);
    case 'dictionary_entry':
      return formatDictionaryPreview(content);
    case 'image':
      return formatImagePreview(content);
    case 'audio':
      return formatAudioPreview(content);
    case 'video':
      return formatVideoPreview(content);
    case 'code':
      return formatCodePreview(content);
    case 'equation':
      return formatEquationPreview(content);
    case 'html':
      return formatHtmlPreview(content);
    default:
      return { main: content.content_value };
  }
}

// Text Content
function formatTextPreview(content: FlashcardContent): ContentPreview {
  const text = content.content_value.trim();
  // Truncate if too long
  const preview = text.length > 100 ? text.substring(0, 100) + '...' : text;
  return { main: preview };
}

// Dictionary Entry Content
//
// The front and back of a vocab card can BOTH carry dictionary_entry metadata
// (the back gets it from the picked definition — word, phonetic, meanings).
// Discriminate by content.side, not by 'metadata.word is present', otherwise
// the back renders as "<word> <phonetic>" instead of the actual definition.
function formatDictionaryPreview(content: FlashcardContent): ContentPreview {
  const metadata = content.content_metadata;

  // Front of a vocab card: show the word itself.
  if (content.side === 'front' && metadata?.word) {
    return {
      main: metadata.word,
      subtitle: metadata.phonetic || content.hint || undefined,
    };
  }

  // Back of a vocab card: show the definition text (cleaned).
  const cleanedText = (content.content_value || '')
    .replace(/\*\*/g, '') // Remove bold markers
    .replace(/\*/g, '')   // Remove italic markers
    .split('\n')
    .filter(line => line.trim().length > 0 && !line.match(/^\d+\./)) // Remove empty lines and numbered items
    .slice(0, 2) // First 2 meaningful lines
    .join(' • ');

  return {
    main: cleanedText || 'Definition',
    subtitle: metadata?.phonetic,
  };
}

// Image Content
function formatImagePreview(content: FlashcardContent): ContentPreview {
  const metadata = content.content_metadata;
  return {
    main: `🖼️ ${metadata?.caption || 'Image'}`,
    subtitle: metadata?.description,
  };
}

// Audio Content
function formatAudioPreview(content: FlashcardContent): ContentPreview {
  const metadata = content.content_metadata;
  return {
    main: `🔊 ${metadata?.title || 'Audio'}`,
    subtitle: metadata?.duration,
  };
}

// Video Content
function formatVideoPreview(content: FlashcardContent): ContentPreview {
  const metadata = content.content_metadata;
  return {
    main: `🎬 ${metadata?.title || 'Video'}`,
    subtitle: metadata?.duration,
  };
}

// Code Content
function formatCodePreview(content: FlashcardContent): ContentPreview {
  const metadata = content.content_metadata;
  const language = metadata?.language || 'code';
  const firstLine = content.content_value.split('\n')[0];
  return {
    main: `💻 ${firstLine}...`,
    subtitle: language,
  };
}

// Equation Content
function formatEquationPreview(content: FlashcardContent): ContentPreview {
  return {
    main: `📐 ${content.content_value}`,
  };
}

// HTML Content
function formatHtmlPreview(content: FlashcardContent): ContentPreview {
  // Strip HTML tags for preview
  const text = content.content_value.replace(/<[^>]*>/g, '');
  const preview = text.length > 100 ? text.substring(0, 100) + '...' : text;
  return { main: preview };
}

// ==================== Full Display (for flashcard viewer) ====================

interface ContentRendererProps {
  content: FlashcardContent;
}

/**
 * Registry of content-type renderers, consulted by `ContentRenderer` before
 * falling back to a plain text display. Pre-populated with the built-ins;
 * other features (e.g. music) register additional types via
 * `registerContentRenderer` at module load.
 */
const contentRenderers: Partial<Record<ContentType, React.FC<ContentRendererProps>>> = {
  text: TextContentRenderer,
  dictionary_entry: DictionaryContentRenderer,
  image: ImageContentRenderer,
  code: CodeContentRenderer,
  equation: EquationContentRenderer,
};

export function registerContentRenderer(type: ContentType, component: React.FC<ContentRendererProps>) {
  contentRenderers[type] = component;
}

/**
 * Render full content in the flashcard viewer
 */
export function ContentRenderer({ content }: ContentRendererProps) {
  const Renderer = contentRenderers[content.content_type];
  if (Renderer) return <Renderer content={content} />;
  return <Text>{content.content_value}</Text>;
}

// ==================== Answer Aid Registry ====================

export interface AnswerAidProps {
  card: Flashcard;
  onReveal: () => void;
}

/**
 * Registry of optional per-content-type "answer aid" widgets shown while a
 * card is unflipped in study mode (e.g. a piano keyboard or note-name picker
 * for music cards). Tapping the aid reveals the answer — it never grades.
 * Other features (e.g. music) register aids via `registerAnswerAid` at
 * module load, keeping flashcards free of feature-specific imports.
 */
const answerAids: Partial<Record<ContentType, React.FC<AnswerAidProps>>> = {};

export function registerAnswerAid(type: ContentType, component: React.FC<AnswerAidProps>) {
  answerAids[type] = component;
}

export function getAnswerAid(type: ContentType): React.FC<AnswerAidProps> | undefined {
  return answerAids[type];
}

// Text Content Renderer
//
// Handles three shapes:
//   1. Short label / single word — the front of a vocab card. Big + bold.
//   2. Definition + "Used in: <sentence>" — the back of an auto-generated
//      vocab card. Split into two blocks with a divider.
//   3. Generic multi-line text — preserves \n so line breaks aren't
//      collapsed into a single run.
function TextContentRenderer({ content }: ContentRendererProps) {
  const text = content.content_value ?? '';

  // Case 1: short label — treat as the front of a vocab card.
  if (text.length < 40 && !text.includes('\n')) {
    return (
      <Text
        className="flashcard-front-label"
        fw={700}
        ta="center"
        fz={56}
        lh={1.15}
      >
        {text}
      </Text>
    );
  }

  // Case 2: definition + example — backend emits
  //   "<definition>\n\nUsed in: \"<sentence>\""
  const usedInMatch = text.match(/^([\s\S]+?)\n\nUsed in:\s*"?([\s\S]+?)"?\s*$/);
  if (usedInMatch) {
    const definition = usedInMatch[1].trim();
    const sentence = usedInMatch[2].trim();
    return (
      <Stack gap="lg" align="center" maw={720} mx="auto">
        <Text
          className="flashcard-back-definition"
          fz={28}
          ta="center"
        >
          {definition}
        </Text>
        <Divider label="Example" labelPosition="center" w="100%" />
        <Text
          className="flashcard-back-example"
          fz={24}
          c="dimmed"
          fs="italic"
          ta="center"
        >
          &ldquo;{sentence}&rdquo;
        </Text>
      </Stack>
    );
  }

  // Case 3: generic multi-line text — preserve line breaks, keep it readable.
  return (
    <Text
      className="flashcard-back-multiline"
      fw={400}
      ta="center"
      fz={26}
      lh={1.45}
    >
      {text}
    </Text>
  );
}

// Dictionary Entry Content Renderer
function DictionaryContentRenderer({ content }: ContentRendererProps) {
  const metadata = content.content_metadata;

  // Render from content_metadata - meanings will always be there for dictionary entries
  return (
    <Box w="100%" ta="left" maw={600} mx="auto" fz={18} style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
      {/* Meanings - each part of speech with up to 2 definitions, no examples */}
      {metadata?.meanings?.map((meaning: { partOfSpeech: string; definitions: Array<{ definition: string; example?: string; synonyms?: string[]; antonyms?: string[] }> }, meaningIdx: number) => (
        <Box key={meaningIdx} mb="md">
          <Text fs="italic" c="blue.6" mb="xs" fw={500}>
            {meaning.partOfSpeech}
          </Text>
          {meaning.definitions.slice(0, 2).map((def: { definition: string; example?: string }, defIdx: number) => (
            <Text key={defIdx} mb="xs" ml={20}>
              {defIdx + 1}. {def.definition}
            </Text>
          ))}
        </Box>
      ))}
    </Box>
  );
}

// Image Content Renderer
function ImageContentRenderer({ content }: ContentRendererProps) {
  const metadata = content.content_metadata;
  return (
    <Box ta="center">
      <Image
        src={content.content_value}
        alt={metadata?.caption || 'Flashcard image'}
        maw="100%"
        mah={400}
        radius="md"
        fit="contain"
      />
      {metadata?.caption && (
        <Text size="sm" c="dimmed" mt="sm">{metadata.caption}</Text>
      )}
    </Box>
  );
}

// Code Content Renderer
function CodeContentRenderer({ content }: ContentRendererProps) {
  const metadata = content.content_metadata;
  const language = metadata?.language || 'plaintext';

  return (
    <Box ta="left" w="100%">
      {language && (
        <Text size="xs" c="dimmed" mb="xs" tt="uppercase">{language}</Text>
      )}
      <Code block p="md" fz="sm">
        {content.content_value}
      </Code>
    </Box>
  );
}

// Equation Content Renderer
function EquationContentRenderer({ content }: ContentRendererProps) {
  return (
    <Text ta="center" fz={24} ff="serif">
      {content.content_value}
    </Text>
  );
}

// ==================== Utility Functions ====================

/**
 * Get a safe display string for any content (fallback)
 */
export function getSafeDisplayText(content: FlashcardContent | string | unknown): string {
  if (typeof content === 'object' && content !== null && 'content_value' in content) {
    return getContentPreview(content as FlashcardContent).main;
  }
  if (typeof content === 'string') {
    return content;
  }
  return JSON.stringify(content);
}

/**
 * Check if content has audio available
 */
export function hasAudio(content: FlashcardContent): boolean {
  return !!(content.content_metadata?.audio_url);
}

/**
 * Get audio URL from content
 */
export function getAudioUrl(content: FlashcardContent): string | undefined {
  return content.content_metadata?.audio_url;
}
